import React, {
  useState, useMemo, useEffect,
} from 'react'
import clsx from 'clsx'
import { ReactComponent as IconSearch } from 'images/search.svg';
import Button from 'components/shared/Button';
import { Input } from 'components/shared/Input'
import Modal from 'components/shared/modals/Modal';
import Loader from 'components/shared/Loader/Loader';
import { useInView } from 'react-intersection-observer';
import styles from './SelectModal.module.scss';

type SelectModalAssetsProps = {
  title: string,
  options: any[],
  btnText?: string,
  isOpen?: boolean,
  isSearchable?: boolean,
  multiple?: boolean,
  showIcons?: boolean,
  onClose?: () => void,
  onSelect: (v: any) => void,
  searchFunc?: (v: string) => (v: any) => boolean,
  ItemComponent?: React.FC<{ data: any }>,
  value?: any,
  notFoundMessage?: React.ReactNode,
  notFoundButton?: any,
  noBtn?: boolean,
  maxLength?: number,
  search?: string
  setSearch?: any
  isLoading?: boolean,
  hasNext?: boolean,
  onNextPage?: (page: number) => void,
}

function SelectModalAssets({
  title,
  options,
  btnText = 'Select',
  isOpen,
  isSearchable,
  multiple,
  showIcons,
  onClose = () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  searchFunc,
  ItemComponent,
  onSelect,
  value,
  notFoundMessage,
  notFoundButton,
  noBtn,
  maxLength,
  search,
  setSearch,
  isLoading,
  hasNext,
  onNextPage,
}: SelectModalAssetsProps) {
  const { ref: nextRef, inView } = useInView()
  const [selectedOptions, setSelectedOptions] = useState<any[]>((multiple ? value : [value]) || [])

  const optionsFiltered = useMemo(() => options, [options, search])

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch((e.target as HTMLInputElement).value)
  }

  const handleSelection = (v: any) => {
    if (noBtn) {
      onSelect(v)
    }
    if (multiple) {
      setSelectedOptions((old: any[]) => {
        const idx = old.findIndex((oldItem: any) => oldItem?.id === v?.id)

        if (idx > -1) {
          const res = [...old]
          res.splice(idx, 1)
          return res
        }

        if (maxLength && old.length > maxLength - 1) {
          return old
        }

        return [...old, v]
      })
    } else {
      setSelectedOptions((old: any[]) => (
        (old[0]?.id === v?.id) ? [] : [v]
      ))
    }
  }

  useEffect(() => {
    if (inView && hasNext && onNextPage) {
      onNextPage(Math.ceil(optionsFiltered.length / 20))
    }
  }, [inView, hasNext])

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      className={styles.container}
      onClick={(e: any) => e.stopPropagation()}
    >
      <div className={styles.title}>{title}</div>

      {isSearchable ? (
        <Input
          icon={<IconSearch />}
          className={styles.searchInput}
          value={search}
          onChange={handleSearchChange}
          placeholder="Search on Beautify"
        />
      ) : null}

      {(isSearchable && search && !optionsFiltered?.length && !isLoading) ? (
        <>
          {notFoundMessage}
          {notFoundButton ? (
            <Button
              className={styles.btn}
              onClick={
                () => notFoundButton?.onClick({
                  options,
                  multiple,
                  onClose,
                  value,
                })
              }
              wide
            >
              {notFoundButton?.text}
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <div className={clsx(styles.options, 'scroll scroll-y')}>
            {optionsFiltered.map((item, idx) => {
              const Icon = item.icon
              const isActive = selectedOptions?.find((v: any) => (v?.id === item?.id))
              return (
                <div
                  key={item.id}
                  className={styles.option}
                  onClick={() => handleSelection(item)}
                >
                  {ItemComponent ? (
                    <>
                      {idx === ((optionsFiltered?.length || 1) - 1) && hasNext ? (
                        <div ref={nextRef} />
                      ) : null}
                      <ItemComponent data={item} />
                    </>
                  ) : (
                    <>
                      {showIcons ? (
                        <div className={styles.icon}>
                          <Icon />
                        </div>
                      ) : null}
                      <span>{item.name}</span>
                    </>
                  )}
                  {noBtn ? null : (
                    <div
                      className={clsx(
                        styles.check,
                        isActive && !multiple && styles.checkActive,
                        isActive && multiple && styles.checkMultipleActive,
                      )}
                    />
                  )}
                </div>
              )
            })}
            {
              isLoading && <Loader className={optionsFiltered?.length ? styles.moreLoading : ''} />
            }
          </div>

          {noBtn ? null : (
            <Button
              className={styles.btn}
              onClick={() => onSelect(multiple ? selectedOptions : selectedOptions[0])}
              wide
            >
              {btnText}
            </Button>
          )}
        </>
      )}
    </Modal>
  )
}

export default SelectModalAssets
